/** Section links are presentation navigation; section IDs come from the checked Worker projection. */
import { useLayoutEffect, useRef } from 'react'
import type { ShopifyStudyBuilderEvaluationResponse } from '@testfeed/shopify-contracts'

export const studyBuilderSectionAnchorId = (sectionId: string) => `section-${sectionId}`

function requestedSection() {
  const url = new URL(window.location.href)
  // Shopify does not forward a cold parent URL's fragment into the iframe. Shared embedded links use the query.
  return url.searchParams.get('section') ?? (url.hash.startsWith('#section-') ? url.hash.slice(9) : null)
}

/** Mounted only once the page is ready and its sections exist. Later evaluations must not pull the reader back. */
export function useStudyBuilderSectionLink(sections: ShopifyStudyBuilderEvaluationResponse['sections']) {
  const root = useRef<HTMLDivElement>(null)
  const revealed = useRef<string | null>(null)
  useLayoutEffect(() => {
    let frame: number | null = null
    const reveal = () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      const section = requestedSection()
      if (!section) {
        revealed.current = null
        return
      }
      if (revealed.current === section || !sections.some((item) => item.id === section)) return
      const target = document.getElementById(studyBuilderSectionAnchorId(section))
      if (!target || !root.current?.contains(target)) return
      frame = window.requestAnimationFrame(() => {
        if (!target.isConnected || requestedSection() !== section) return
        target.scrollIntoView({ block: 'start', behavior: 'instant' })
        revealed.current = section
      })
    }
    reveal()
    window.addEventListener('hashchange', reveal)
    window.addEventListener('popstate', reveal)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      window.removeEventListener('hashchange', reveal)
      window.removeEventListener('popstate', reveal)
    }
  }, [sections])
  return root
}
